import type { CSSProperties, ReactElement } from "react";
import { Badge, type BadgeVariant } from "@primitives/Badge";
import { Text } from "@primitives/Text";
import { radius } from "@tokens/index";

// ---------------------------------------------------------------------------
// TallyBadge — the Completed / Open / Pending chip shared by every surface
// that shows WorkItemTally buckets.
//
// One bucket -> variant mapping keeps the entity workflow list and the queue
// tree aligned. The chip reads "<count> <label>" with tabular numerals so
// stacked chips align regardless of digit width. `total` is the compact-class
// rollup of the open and pending buckets.
// ---------------------------------------------------------------------------

export type TallyBucket = "completed" | "open" | "pending" | "total";

const BUCKET_LABEL: Record<TallyBucket, string> = {
    completed: "Completed",
    open: "Open",
    pending: "Pending",
    total: "Items",
};

const BUCKET_VARIANT: Record<TallyBucket, BadgeVariant> = {
    completed: "success",
    open: "warning",
    pending: "accent",
    total: "neutral",
};

export interface TallyBadgeProps {
    readonly bucket: TallyBucket;
    readonly count: number;
    /**
     * When provided, the chip becomes a button (click / Enter / Space) that
     * fires this callback to drill into the work items behind the count.
     * When omitted the chip is a passive indicator.
     */
    readonly onActivate?: () => void;
    /**
     * Accessible label for the button variant (for example "View 8 open work
     * items in Send RateCon"). Ignored when `onActivate` is absent.
     */
    readonly activateLabel?: string;
}

// The button shrink-wraps the Badge so the chip looks the same whether or
// not it is interactive. `data-hit-area` extends the pointer target to 40px
// without changing the layout box.
const buttonStyle: CSSProperties = {
    appearance: "none",
    position: "relative",
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    display: "inline-flex",
    borderRadius: radius.pill,
    font: "inherit",
    color: "inherit",
};

export function TallyBadge({
    bucket,
    count,
    onActivate,
    activateLabel,
}: TallyBadgeProps): ReactElement {
    const badge = (
        <Badge variant={BUCKET_VARIANT[bucket]} size="sm" caps={false}>
            <Text role="bodySm" size="xs" tabular style={{ lineHeight: 1, color: "inherit" }}>
                {String(count)} {BUCKET_LABEL[bucket]}
            </Text>
        </Badge>
    );

    if (onActivate === undefined) {
        return badge;
    }

    return (
        <button
            type="button"
            data-hit-area=""
            style={buttonStyle}
            aria-label={
                activateLabel ??
                `View ${String(count)} ${BUCKET_LABEL[bucket].toLowerCase()} work items`
            }
            onClick={(e) => {
                // The chip is its own target inside an interactive row.
                e.stopPropagation();
                onActivate();
            }}
        >
            {badge}
        </button>
    );
}
